import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MessageSquare, Clock, Monitor, DollarSign, TrendingUp, Zap, Users, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle, Card, Badge, Button,
  Select, StatCard, StatValue, StatLabel, Grid, Flex, LoadingContainer, Spinner, SectionTitle
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';


const formatKPIValue = (value, type) => {
  if (type === 'money') return `R$ ${value.toLocaleString()}`;
  if (type === 'rating') return value.toFixed(1);
  return value;
};

const ManagerDashboard = () => {
  const { t } = useTranslation('manager');
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab]       = useState('metrics');
  const [metrics, setMetrics]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [agents, setAgents]             = useState([]);
  const [billing, setBilling]           = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingMonth, setBillingMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [actionStats, setActionStats]   = useState(null);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsMonth, setActionsMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [actionFilterTool, setActionFilterTool] = useState('');
  const [actionFilterCT, setActionFilterCT] = useState('');
  const [humanMetrics, setHumanMetrics] = useState(null);
  const [humanLoading, setHumanLoading] = useState(false);
  const [humanFrom, setHumanFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [humanTo, setHumanTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsMonth, setSessionsMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const homeItem = isSallusAdmin
    ? { label: 'Libben Agents', path: '/sallus/dashboard' }
    : user.role === 'manager'
      ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
      : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` };

  const breadcrumbItems = [homeItem, { label: t('title') }];

  const loadMetrics = useCallback(async () => {
    try {
      const params = selectedAgent !== 'all' ? `?agentId=${selectedAgent}` : '';
      const res = await api.get(`/dashboard/${tenantId}/metrics${params}`);
      const data = res.data;
      const overview = {
        totalConversations: data.overview?.totalConversations || 0,
        activeSessions: data.overview?.activeSessions || 0
      };
      const agentKpis = {};
      for (const a of (data.agentMetrics || [])) {
        agentKpis[a.agentId] = {
          agentName: a.agentId,
          kpis: [
            { name: t('kpiConversations'), current: a.totalConversations, target: 50, unit: '', type: 'counter', color: theme.colors.success },
            { name: t('kpiMessages'), current: a.messageCount, target: 200, unit: '', type: 'counter', color: theme.colors.warning }
          ]
        };
      }
      setMetrics({ all: overview, ...agentKpis });
    } catch (e) {
      console.error(e);
      setMetrics({ all: { totalConversations: 0, activeSessions: 0 } });
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedAgent]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBilling = useCallback(async () => {
    setBillingLoading(true);
    try {
      const res = await api.get(`/usage/${tenantId}?month=${billingMonth}&breakdown=true`);
      setBilling(res.data);
    } catch (e) { console.error(e); }
    finally { setBillingLoading(false); }
  }, [tenantId, billingMonth]);

  const loadActionStats = useCallback(async () => {
    setActionsLoading(true);
    try {
      const params = new URLSearchParams({ month: actionsMonth });
      if (actionFilterTool) params.set('tool', actionFilterTool);
      if (actionFilterCT) params.set('connectorType', actionFilterCT);
      const res = await api.get(`/dashboard/${tenantId}/action-stats?${params.toString()}`);
      setActionStats(res.data);
    } catch (e) { console.error(e); }
    finally { setActionsLoading(false); }
  }, [tenantId, actionsMonth, actionFilterTool, actionFilterCT]);

  const loadHumanMetrics = useCallback(async () => {
    setHumanLoading(true);
    try {
      const res = await api.get(`/dashboard/${tenantId}/human-metrics?from=${humanFrom}&to=${humanTo}`);
      setHumanMetrics(res.data);
    } catch (e) { console.error(e); }
    finally { setHumanLoading(false); }
  }, [tenantId, humanFrom, humanTo]);

  const loadSessionStats = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await api.get(`/dashboard/${tenantId}/session-stats?month=${sessionsMonth}`);
      setSessionStats(res.data);
    } catch (e) { console.error(e); }
    finally { setSessionsLoading(false); }
  }, [tenantId, sessionsMonth]);

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 30000);
    return () => clearInterval(interval);
  }, [loadMetrics]);

  useEffect(() => {
    api.get(`/marketplace/tenants/${tenantId}/agents`)
      .then(res => setAgents((res.data.agents || []).map(a => ({
        id: a.instanceId || a.agentId,
        name: a.instanceName || a.name,
        role: a.category || a.role || ''
      }))))
      .catch(e => console.error('loadAgents error:', e));
  }, [tenantId]);

  useEffect(() => {
    if (activeTab === 'billing') loadBilling();
  }, [activeTab, loadBilling]);

  useEffect(() => {
    if (activeTab === 'actions') loadActionStats();
  }, [activeTab, loadActionStats]);

  useEffect(() => {
    if (activeTab === 'human') loadHumanMetrics();
  }, [activeTab, loadHumanMetrics]);

  useEffect(() => {
    if (activeTab === 'sessions') loadSessionStats();
  }, [activeTab, loadSessionStats]);

  if (loading) return <LoadingContainer><Spinner size="24px" /> {t('loadingMetrics')}</LoadingContainer>;

  const currentMetrics = selectedAgent === 'all' ? metrics?.all : metrics?.[selectedAgent];
  const isAgentView = selectedAgent !== 'all';

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>Dashboard — {tenantId}</PageTitle>
            <PageSubtitle>{t('updatedAt', { time: new Date().toLocaleTimeString() })}</PageSubtitle>
          </div>
          <Flex gap={theme.spacing[3]}>
            <Button variant="secondary" size="sm" onClick={() => navigate(`/monitor/${tenantId}`)}>
              <Monitor size={14} /> {t('monitor')}
            </Button>
          </Flex>
        </PageHeader>

        <TabBar>
          <Tab $active={activeTab === 'metrics'} onClick={() => setActiveTab('metrics')}>{t('tabMetrics')}</Tab>
          <Tab $active={activeTab === 'sessions'} onClick={() => setActiveTab('sessions')}>{t('tabSessions', { defaultValue: 'Sessões IA×Humano' })}</Tab>
          <Tab $active={activeTab === 'human'} onClick={() => setActiveTab('human')}>{t('tabHuman', { defaultValue: 'Atendentes' })}</Tab>
          <Tab $active={activeTab === 'billing'} onClick={() => setActiveTab('billing')}>{t('tabBilling')}</Tab>
          <Tab $active={activeTab === 'actions'} onClick={() => setActiveTab('actions')}>{t('tabActions')}</Tab>
        </TabBar>

        {activeTab === 'metrics' && (
          <>
            <Flex gap={theme.spacing[3]} style={{ marginBottom: theme.spacing[5], marginTop: theme.spacing[5] }}>
              <AgentLabel>{t('view')}</AgentLabel>
              <Select value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)} style={{ width: 'auto' }}>
                <option value="all">{t('viewAll')}</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}{a.role ? ` — ${a.role}` : ''}</option>)}
              </Select>
            </Flex>

            {!isAgentView && (
              <Grid min="220px" style={{ marginBottom: theme.spacing[6] }}>
                <StatCard accent={theme.colors.success}>
                  <Flex gap={theme.spacing[2]}><MessageSquare size={18} color={theme.colors.success} /></Flex>
                  <StatValue>{currentMetrics?.totalConversations || 0}</StatValue>
                  <StatLabel>{t('todayConversations')}</StatLabel>
                </StatCard>
                <StatCard accent={theme.colors.info}>
                  <Flex gap={theme.spacing[2]}><Users size={18} color={theme.colors.info} /></Flex>
                  <StatValue>{currentMetrics?.activeSessions || 0}</StatValue>
                  <StatLabel>{t('activeSessions', { defaultValue: 'Sessões ativas' })}</StatLabel>
                </StatCard>
              </Grid>
            )}

            {!isAgentView && metrics && (
              <Card>
                <SectionTitle>{t('byAgent')}</SectionTitle>
                <TableScroll><AgentTable>
                  <thead>
                    <tr>
                      <Th>{t('colAgent')}</Th>
                      <Th align="right">{t('kpiConversations')}</Th>
                      <Th align="right">{t('kpiMessages')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(metrics)
                      .filter(([k]) => k !== 'all')
                      .map(([agentId, data]) => {
                        const agent = agents.find(a => a.id === agentId);
                        const kpis = data.kpis || [];
                        return (
                          <tr key={agentId} style={{ cursor: 'pointer' }} onClick={() => setSelectedAgent(agentId)}>
                            <Td><AgentChip>{agent?.name || agentId}</AgentChip></Td>
                            <Td align="right">{kpis[0]?.current || 0}</Td>
                            <Td align="right">{kpis[1]?.current || 0}</Td>
                          </tr>
                        );
                      })}
                  </tbody>
                </AgentTable></TableScroll>
              </Card>
            )}

            {isAgentView && currentMetrics && (
              <Grid min="280px" style={{ margin: `${theme.spacing[5]} 0` }}>
                {currentMetrics.kpis.map((kpi, idx) => (
                  <Card key={idx}>
                    <Flex justify="space-between" style={{ marginBottom: theme.spacing[3] }}>
                      <KPIName>{kpi.name}</KPIName>
                      <Badge variant={kpi.current >= kpi.target ? 'success' : 'warning'}>
                        {kpi.current >= kpi.target ? t('goalReached') : t('belowGoal')}
                      </Badge>
                    </Flex>
                    <KPIValue color={kpi.color}>{formatKPIValue(kpi.current, kpi.type)} {kpi.unit}</KPIValue>
                    <ProgressTrack>
                      <ProgressFill width={Math.min((kpi.current / kpi.target) * 100, 100)} color={kpi.color} />
                    </ProgressTrack>
                    <KPITarget>{t('goal', { value: formatKPIValue(kpi.target, kpi.type), unit: kpi.unit })}</KPITarget>
                  </Card>
                ))}
              </Grid>
            )}
          </>
        )}

        {activeTab === 'billing' && (
          <BillingTab billing={billing} loading={billingLoading} month={billingMonth} onMonthChange={setBillingMonth} onReload={loadBilling} t={t} isSallusAdmin={isSallusAdmin} />
        )}

        {activeTab === 'actions' && (
          <ActionsTab
            data={actionStats}
            loading={actionsLoading}
            month={actionsMonth}
            onMonthChange={setActionsMonth}
            onReload={loadActionStats}
            filterTool={actionFilterTool}
            onFilterTool={setActionFilterTool}
            filterCT={actionFilterCT}
            onFilterCT={setActionFilterCT}
            t={t}
          />
        )}

        {activeTab === 'human' && (
          <HumanTab
            data={humanMetrics}
            loading={humanLoading}
            from={humanFrom}
            to={humanTo}
            onFromChange={setHumanFrom}
            onToChange={setHumanTo}
            onReload={loadHumanMetrics}
            t={t}
          />
        )}

        {activeTab === 'sessions' && (
          <SessionsTab
            data={sessionStats}
            loading={sessionsLoading}
            month={sessionsMonth}
            onMonthChange={setSessionsMonth}
            onReload={loadSessionStats}
            t={t}
          />
        )}
      </PageContainer>
    </>
  );
};

function BarChart({ data, valueKey, labelKey, color, emptyLabel }) {
  if (!data || data.length === 0) return <ChartEmpty>{emptyLabel}</ChartEmpty>;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <RechartsBarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray100} />
        <XAxis dataKey={labelKey} tick={{ fontSize: 11 }} tickFormatter={v => String(v).slice(-5)} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey={valueKey} fill={color} radius={[3, 3, 0, 0]} />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

function BillingTab({ billing, loading, month, onMonthChange, onReload, t, isSallusAdmin }) {
  if (loading) return <LoadingContainer><Spinner size="20px" /> {t('loadingUsage')}</LoadingContainer>;

  const byDayArr = billing?.byDay
    ? Object.entries(billing.byDay).map(([day, v]) => ({ day, ...v })).sort((a, b) => a.day.localeCompare(b.day))
    : [];
  const byAgentArr = billing?.byAgent
    ? Object.entries(billing.byAgent).map(([agentId, v]) => ({ agentId, ...v }))
    : [];

  return (
    <div style={{ marginTop: theme.spacing[5] }}>
      <Flex justify="space-between" style={{ marginBottom: theme.spacing[5] }}>
        <div>
          <PageTitle style={{ fontSize: theme.typography.xl }}>{t('usageBilling')}</PageTitle>
          <PageSubtitle>{t('usageSubtitle')}</PageSubtitle>
        </div>
        <Flex gap={theme.spacing[2]}>
          <MonthInput type="month" value={month} onChange={e => onMonthChange(e.target.value)} />
          <Button variant="secondary" size="sm" onClick={onReload}>{t('update')}</Button>
        </Flex>
      </Flex>

      <Grid min="200px" style={{ marginBottom: theme.spacing[6] }}>
        <StatCard accent={theme.colors.primary}>
          <Flex gap={theme.spacing[2]}><MessageSquare size={16} color={theme.colors.primary} /></Flex>
          <StatValue>{(billing?.messages || 0).toLocaleString()}</StatValue>
          <StatLabel><LabelWithHelp label={t('processedMessages')} help={t('processedMessagesHelp')} /></StatLabel>
        </StatCard>
        <StatCard accent={theme.colors.secondary}>
          <Flex gap={theme.spacing[2]}><Zap size={16} color={theme.colors.secondary} /></Flex>
          <StatValue>{((billing?.tokensInput || 0) + (billing?.tokensOutput || 0)).toLocaleString()}</StatValue>
          <StatLabel>{t('bedrockTokens')}</StatLabel>
        </StatCard>
        <StatCard accent={theme.colors.info}>
          <Flex gap={theme.spacing[2]}><Zap size={16} color={theme.colors.info} /></Flex>
          <StatValue>{(billing?.tokensCacheRead || 0).toLocaleString()}</StatValue>
          <StatLabel>{t('cacheRead', { defaultValue: 'Tokens em cache (leitura)' })}</StatLabel>
        </StatCard>
        <StatCard accent={theme.colors.warning}>
          <Flex gap={theme.spacing[2]}><Zap size={16} color={theme.colors.warning} /></Flex>
          <StatValue>{(billing?.tokensCacheWrite || 0).toLocaleString()}</StatValue>
          <StatLabel>{t('cacheWrite', { defaultValue: 'Tokens em cache (escrita)' })}</StatLabel>
        </StatCard>
        <StatCard accent={theme.colors.success}>
          <Flex gap={theme.spacing[2]}><TrendingUp size={16} color={theme.colors.success} /></Flex>
          <StatValue>{billing?.toolCalls || 0}</StatValue>
          <StatLabel>{t('toolCalls')}</StatLabel>
        </StatCard>
        {isSallusAdmin && (
          <StatCard accent={theme.colors.warning}>
            <Flex gap={theme.spacing[2]}><DollarSign size={16} color={theme.colors.warning} /></Flex>
            <StatValue>$ {(billing?.estimatedCostUSD || 0).toFixed(4)}</StatValue>
            <StatLabel>{t('estimatedCost')}</StatLabel>
          </StatCard>
        )}
      </Grid>

      {/* Mensagens de marketing */}
      {(billing?.marketingStats) && (
        <Card style={{ marginBottom: theme.spacing[6] }}>
          <SectionTitle>{t('marketingMessages')}</SectionTitle>
          <Grid min="160px">
            <MktStatBox $color="${theme.colors.channels.whatsapp}">
              <MktVal>{(billing.marketingStats.whatsapp.marketing || 0).toLocaleString()}</MktVal>
              <MktLbl>📱 WhatsApp {t('marketing')}</MktLbl>
            </MktStatBox>
            <MktStatBox $color="#0ea5e9">
              <MktVal>{(billing.marketingStats.whatsapp.utility || 0).toLocaleString()}</MktVal>
              <MktLbl>📱 WhatsApp {t('utility')}</MktLbl>
            </MktStatBox>
            <MktStatBox $color="#f59e0b">
              <MktVal>{(billing.marketingStats.email.marketing || 0).toLocaleString()}</MktVal>
              <MktLbl>📧 Email {t('marketing')}</MktLbl>
            </MktStatBox>
            <MktStatBox $color="#8b5cf6">
              <MktVal>{(billing.marketingStats.email.utility || 0).toLocaleString()}</MktVal>
              <MktLbl>📧 Email {t('utility')}</MktLbl>
            </MktStatBox>
          </Grid>
        </Card>
      )}

      <Grid min="380px" style={{ marginBottom: theme.spacing[6] }}>
        <Card>
          <SectionTitle>{t('messagesByDay')}</SectionTitle>
          <BarChart data={byDayArr} valueKey="messages" labelKey="day" color={theme.colors.primary} emptyLabel={t('noChartData')} />
        </Card>
        {isSallusAdmin && (
          <Card>
            <SectionTitle>{t('costByDay')}</SectionTitle>
            <BarChart data={byDayArr} valueKey="estimatedCostUSD" labelKey="day" color={theme.colors.secondary} emptyLabel={t('noChartData')} />
          </Card>
        )}
      </Grid>

      {byAgentArr.length > 0 && (
        <Card>
          <SectionTitle>{t('byAgent')}</SectionTitle>
          <TableScroll><AgentTable>
            <thead>
              <tr>
                <Th>{t('colAgent')}</Th>
                <Th align="right">{t('colMessages')}</Th>
                <Th align="right">{t('colTokensIn')}</Th>
                <Th align="right">{t('colTokensOut')}</Th>
                <Th align="right">{t('colCacheRead', { defaultValue: 'Cache leitura' })}</Th>
                <Th align="right">{t('colCacheWrite', { defaultValue: 'Cache escrita' })}</Th>
                <Th align="right">{t('colToolCalls')}</Th>
              </tr>
            </thead>
            <tbody>
              {byAgentArr.map(a => (
                <tr key={a.agentId}>
                  <Td><AgentChip>{a.agentId}</AgentChip></Td>
                  <Td align="right">{(a.messages || 0).toLocaleString()}</Td>
                  <Td align="right">{(a.tokensInput || 0).toLocaleString()}</Td>
                  <Td align="right">{(a.tokensOutput || 0).toLocaleString()}</Td>
                  <Td align="right">{(a.tokensCacheRead || 0).toLocaleString()}</Td>
                  <Td align="right">{(a.tokensCacheWrite || 0).toLocaleString()}</Td>
                  <Td align="right">{a.toolCalls || 0}</Td>
                </tr>
              ))}
            </tbody>
          </AgentTable></TableScroll>
        </Card>
      )}
    </div>
  );
}

function ActionsTab({ data, loading, month, onMonthChange, onReload, filterTool, onFilterTool, filterCT, onFilterCT, t }) {
  if (loading) return <LoadingContainer><Spinner size="20px" /> {t('loadingActions')}</LoadingContainer>;

  const stats = data?.stats || [];
  const byDay = data?.byDay || [];

  // Extrair listas únicas de tools e connectorTypes para os filtros
  const allTools = [...new Set(stats.map(s => s.tool))].sort();
  const allCTs = [...new Set(stats.map(s => s.connectorType))].sort();

  // Agregar byDay por dia (somando todas as tools/connectors filtrados)
  const dayMap = {};
  for (const entry of byDay) {
    if (!dayMap[entry.day]) dayMap[entry.day] = 0;
    dayMap[entry.day] += entry.count;
  }
  const byDayArr = Object.entries(dayMap).map(([day, count]) => ({ day, count })).sort((a, b) => a.day.localeCompare(b.day));

  return (
    <div style={{ marginTop: theme.spacing[5] }}>
      <Flex justify="space-between" style={{ marginBottom: theme.spacing[5] }}>
        <div>
          <PageTitle style={{ fontSize: theme.typography.xl }}>{t('actionStatsTitle')}</PageTitle>
          <PageSubtitle>{t('actionStatsSubtitle')}</PageSubtitle>
        </div>
        <Flex gap={theme.spacing[2]}>
          <MonthInput type="month" value={month} onChange={e => onMonthChange(e.target.value)} />
          <Button variant="secondary" size="sm" onClick={onReload}>{t('update')}</Button>
        </Flex>
      </Flex>

      <Flex gap={theme.spacing[3]} style={{ marginBottom: theme.spacing[5], flexWrap: 'wrap' }}>
        <Flex gap={theme.spacing[2]} align="center">
          <AgentLabel>{t('filterTool')}</AgentLabel>
          <Select value={filterTool} onChange={e => onFilterTool(e.target.value)} style={{ width: 'auto' }}>
            <option value="">{t('allTools')}</option>
            {allTools.map(tool => <option key={tool} value={tool}>{tool}</option>)}
          </Select>
        </Flex>
        <Flex gap={theme.spacing[2]} align="center">
          <AgentLabel>{t('filterConnector')}</AgentLabel>
          <Select value={filterCT} onChange={e => onFilterCT(e.target.value)} style={{ width: 'auto' }}>
            <option value="">{t('allConnectors')}</option>
            {allCTs.map(ct => <option key={ct} value={ct}>{ct}</option>)}
          </Select>
        </Flex>
      </Flex>

      {stats.length === 0 ? (
        <Card><ChartEmpty>{t('noActionData')}</ChartEmpty></Card>
      ) : (
        <>
          <Grid min="200px" style={{ marginBottom: theme.spacing[6] }}>
            {stats.slice(0, 6).map((s, idx) => (
              <StatCard key={idx} accent={ACTION_COLORS[idx % ACTION_COLORS.length]}>
                <StatValue>{s.count.toLocaleString()}</StatValue>
                <StatLabel>{s.tool}</StatLabel>
                <ActionConnectorLabel>{s.connectorType}</ActionConnectorLabel>
              </StatCard>
            ))}
          </Grid>

          <Card style={{ marginBottom: theme.spacing[6] }}>
            <SectionTitle>{t('actionsByDay')}</SectionTitle>
            <BarChart data={byDayArr} valueKey="count" labelKey="day" color={theme.colors.primary} emptyLabel={t('noActionData')} />
          </Card>

          {stats.length > 0 && (
            <Card>
              <SectionTitle>{t('actionStatsTitle')}</SectionTitle>
              <TableScroll><AgentTable>
                <thead>
                  <tr>
                    <Th>{t('colTool')}</Th>
                    <Th>{t('colConnectorType')}</Th>
                    <Th align="right">{t('colCount')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s, idx) => (
                    <tr key={idx}>
                      <Td><AgentChip>{s.tool}</AgentChip></Td>
                      <Td>{s.connectorType}</Td>
                      <Td align="right">{s.count.toLocaleString()}</Td>
                    </tr>
                  ))}
                </tbody>
              </AgentTable></TableScroll>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

const HUMAN_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6'];

const OUTCOME_LABELS = {
  resolved: 'Resolvida (IA)',
  resolved_human: 'Resolvida (humano)',
  completed: 'Concluída',
  timeout_abandoned: 'Abandonada (inatividade)',
  terminated_by_manager: 'Encerrada (gestor)',
  open: 'Em aberto',
};

function SessionsTab({ data, loading, month, onMonthChange, onReload, t }) {
  if (loading) return <LoadingContainer><Spinner size="20px" /> {t('loadingMetrics')}</LoadingContainer>;

  const total = data?.total || 0;
  const aiOnly = data?.aiOnly || 0;
  const migrated = data?.migratedToHuman || 0;
  const rate = data?.migrationRate || 0;
  const daily = data?.daily || [];
  const byOutcome = data?.byOutcome || {};

  const chartData = daily.map(d => ({ date: d.date.slice(5), IA: d.ai, Humano: d.human }));
  const outcomeRows = Object.entries(byOutcome).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ marginTop: theme.spacing[5] }}>
      <Flex gap={theme.spacing[3]} align="center" style={{ marginBottom: theme.spacing[5] }}>
        <AgentLabel>{t('sessMonth', { defaultValue: 'Mês' })}</AgentLabel>
        <MonthInput type="month" value={month} onChange={e => onMonthChange(e.target.value)} />
        <Button variant="secondary" size="sm" onClick={onReload}>
          <TrendingUp size={14} /> {t('sessRefresh', { defaultValue: 'Atualizar' })}
        </Button>
      </Flex>

      {total === 0 ? (
        <Card><ChartEmpty>{t('sessNoData', { defaultValue: 'Nenhuma sessão registrada no período' })}</ChartEmpty></Card>
      ) : (
        <>
          <Grid min="200px" style={{ marginBottom: theme.spacing[6] }}>
            <StatCard accent={theme.colors.info}>
              <Flex gap={theme.spacing[2]}><MessageSquare size={18} color={theme.colors.info} /></Flex>
              <StatValue>{total}</StatValue>
              <StatLabel>{t('sessTotal', { defaultValue: 'Total de sessões' })}</StatLabel>
            </StatCard>
            <StatCard accent={theme.colors.success}>
              <Flex gap={theme.spacing[2]}><Zap size={18} color={theme.colors.success} /></Flex>
              <StatValue>{aiOnly}</StatValue>
              <StatLabel>{t('sessAi', { defaultValue: 'Resolvidas pela IA' })}</StatLabel>
            </StatCard>
            <StatCard accent={theme.colors.warning}>
              <Flex gap={theme.spacing[2]}><Users size={18} color={theme.colors.warning} /></Flex>
              <StatValue>{migrated}</StatValue>
              <StatLabel>{t('sessHuman', { defaultValue: 'Migraram p/ humano' })}</StatLabel>
            </StatCard>
            <StatCard accent={theme.colors.primary}>
              <Flex gap={theme.spacing[2]}><TrendingUp size={18} color={theme.colors.primary} /></Flex>
              <StatValue>{rate}%</StatValue>
              <StatLabel>{t('sessRate', { defaultValue: 'Taxa de migração' })}</StatLabel>
            </StatCard>
          </Grid>

          {chartData.length > 0 && (
            <Card style={{ marginBottom: theme.spacing[6] }}>
              <SectionTitle>{t('sessChartTitle', { defaultValue: 'Sessões por dia — IA vs Humano' })}</SectionTitle>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray100} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="IA" stroke={theme.colors.success} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Humano" stroke={theme.colors.warning} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Card>
            <SectionTitle>{t('sessOutcomeTitle', { defaultValue: 'Desfechos das sessões' })}</SectionTitle>
            <TableScroll><AgentTable>
              <thead>
                <tr>
                  <Th>{t('sessColOutcome', { defaultValue: 'Desfecho' })}</Th>
                  <Th align="right">{t('sessColCount', { defaultValue: 'Sessões' })}</Th>
                  <Th align="right">{t('sessColPct', { defaultValue: '%' })}</Th>
                </tr>
              </thead>
              <tbody>
                {outcomeRows.map(([outcome, count]) => (
                  <tr key={outcome}>
                    <Td><AgentChip>{OUTCOME_LABELS[outcome] || outcome}</AgentChip></Td>
                    <Td align="right">{count}</Td>
                    <Td align="right">{total > 0 ? Math.round((count / total) * 1000) / 10 : 0}%</Td>
                  </tr>
                ))}
              </tbody>
            </AgentTable></TableScroll>
          </Card>
        </>
      )}
    </div>
  );
}

function HumanTab({ data, loading, from, to, onFromChange, onToChange, onReload, t }) {
  if (loading) return <LoadingContainer><Spinner size="20px" /> {t('loadingMetrics')}</LoadingContainer>;

  const summary = data?.summary || [];
  const daily = data?.daily || [];

  // Build chart data: pivot daily array into { date, agent1: n, agent2: n, ... }
  const allDates = [...new Set(daily.map(d => d.date))].sort();
  const allAgents = [...new Set(daily.map(d => d.memberId))];
  const chartData = allDates.map(date => {
    const row = { date: date.slice(5) }; // MM-DD for display
    for (const agent of allAgents) {
      const entry = daily.find(d => d.date === date && d.memberId === agent);
      row[agent] = entry?.sessions || 0;
    }
    return row;
  });

  // Agent display names from summary
  const agentNames = {};
  for (const s of summary) {
    agentNames[s.memberId] = s.humanAgentId || s.memberId;
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0min';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins}min`;
    return `${Math.floor(mins / 60)}h${mins % 60}min`;
  };

  return (
    <div style={{ marginTop: theme.spacing[5] }}>
      <Flex gap={theme.spacing[3]} align="center" style={{ marginBottom: theme.spacing[5] }}>
        <AgentLabel>{t('humanPeriod', { defaultValue: 'Período' })}</AgentLabel>
        <MonthInput type="date" value={from} onChange={e => onFromChange(e.target.value)} />
        <span style={{ color: theme.colors.textMuted }}>—</span>
        <MonthInput type="date" value={to} onChange={e => onToChange(e.target.value)} />
        <Button variant="secondary" size="sm" onClick={onReload}>
          <TrendingUp size={14} /> {t('humanRefresh', { defaultValue: 'Atualizar' })}
        </Button>
      </Flex>

      {summary.length === 0 ? (
        <Card><ChartEmpty>{t('humanNoData', { defaultValue: 'Nenhum atendimento humano registrado no período' })}</ChartEmpty></Card>
      ) : (
        <>
          <Grid min="200px" style={{ marginBottom: theme.spacing[6] }}>
            <StatCard accent={theme.colors.success}>
              <Flex gap={theme.spacing[2]}><Users size={18} color={theme.colors.success} /></Flex>
              <StatValue>{summary.length}</StatValue>
              <StatLabel>{t('humanAgentCount', { defaultValue: 'Atendentes ativos' })}</StatLabel>
            </StatCard>
            <StatCard accent={theme.colors.info}>
              <Flex gap={theme.spacing[2]}><MessageSquare size={18} color={theme.colors.info} /></Flex>
              <StatValue>{summary.reduce((s, a) => s + a.totalSessions, 0)}</StatValue>
              <StatLabel><LabelWithHelp label={t('humanTotalSessions', { defaultValue: 'Total atendimentos' })} help={t('humanTotalSessionsHelp')} /></StatLabel>
            </StatCard>
            <StatCard accent={theme.colors.warning}>
              <Flex gap={theme.spacing[2]}><Clock size={18} color={theme.colors.warning} /></Flex>
              <StatValue>{formatDuration(Math.round(summary.reduce((s, a) => s + a.avgDurationSeconds, 0) / summary.length))}</StatValue>
              <StatLabel>{t('humanAvgDuration', { defaultValue: 'Tempo médio' })}</StatLabel>
            </StatCard>
            <StatCard accent={theme.colors.primary}>
              <Flex gap={theme.spacing[2]}><Clock size={18} color={theme.colors.primary} /></Flex>
              <StatValue>{formatDuration(Math.round(summary.reduce((s, a) => s + a.avgWaitTimeSeconds, 0) / summary.length))}</StatValue>
              <StatLabel>{t('humanAvgWait', { defaultValue: 'Espera média na fila' })}</StatLabel>
            </StatCard>
          </Grid>

          {chartData.length > 0 && (
            <Card style={{ marginBottom: theme.spacing[6] }}>
              <SectionTitle>{t('humanChartTitle', { defaultValue: 'Atendimentos por dia' })}</SectionTitle>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray100} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {allAgents.map((agent, idx) => (
                    <Line
                      key={agent}
                      type="monotone"
                      dataKey={agent}
                      name={agentNames[agent] || agent}
                      stroke={HUMAN_COLORS[idx % HUMAN_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Card>
            <SectionTitle>{t('humanTableTitle', { defaultValue: 'Desempenho por atendente' })}</SectionTitle>
            <TableScroll><AgentTable>
              <thead>
                <tr>
                  <Th>{t('humanColName', { defaultValue: 'Atendente' })}</Th>
                  <Th align="right">{t('humanColSessions', { defaultValue: 'Atendimentos' })}</Th>
                  <Th align="right">{t('humanColMessages', { defaultValue: 'Msgs/atend.' })}</Th>
                  <Th align="right">{t('humanColDuration', { defaultValue: 'Tempo médio' })}</Th>
                  <Th align="right">{t('humanColWait', { defaultValue: 'Espera fila' })}</Th>
                </tr>
              </thead>
              <tbody>
                {summary.sort((a, b) => b.totalSessions - a.totalSessions).map((agent, idx) => (
                  <tr key={agent.memberId}>
                    <Td><AgentChip>{agentNames[agent.memberId] || agent.memberId}</AgentChip></Td>
                    <Td align="right">{agent.totalSessions}</Td>
                    <Td align="right">{agent.avgMessages}</Td>
                    <Td align="right">{formatDuration(agent.avgDurationSeconds)}</Td>
                    <Td align="right">{formatDuration(agent.avgWaitTimeSeconds)}</Td>
                  </tr>
                ))}
              </tbody>
            </AgentTable></TableScroll>
          </Card>
        </>
      )}
    </div>
  );
}

const ACTION_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
const ActionConnectorLabel = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 2px;`;

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const AgentLabel = styled.span`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; white-space: nowrap;`;
// SectionTitle — importado de ui.js
const KPIName = styled.h3`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary}; margin: 0;`;
const KPIValue = styled.div`font-size: ${theme.typography['3xl']}; font-weight: ${theme.typography.bold}; color: ${p => p.color}; margin-bottom: ${theme.spacing[3]};`;
const ProgressTrack = styled.div`width: 100%; height: 6px; background: ${theme.colors.gray100}; border-radius: ${theme.radii.full}; overflow: hidden; margin-bottom: ${theme.spacing[2]};`;
const ProgressFill = styled.div`width: ${p => p.width}%; height: 100%; background: ${p => p.color}; transition: width 0.3s ease;`;
const KPITarget = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const TabBar = styled.div`display: flex; gap: 2px; background: ${theme.colors.white}; border-bottom: 1px solid ${theme.colors.border}; padding: 0 ${theme.spacing[6]};`;
const Tab = styled.button`padding: ${theme.spacing[3]} ${theme.spacing[5]}; background: none; border: none; border-bottom: 3px solid ${p => p.$active ? theme.colors.primary : 'transparent'}; color: ${p => p.$active ? theme.colors.primary : theme.colors.textMuted}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; cursor: pointer; transition: ${theme.transitions.fast}; &:hover { color: ${theme.colors.primary}; }`;
const MonthInput = styled.input`padding: ${theme.spacing[1]} ${theme.spacing[2]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; &:focus { outline: none; border-color: ${theme.colors.primary}; }`;
const MktStatBox = styled.div`background: ${p => p.$color}18; border: 1px solid ${p => p.$color}33; border-radius: ${theme.radii.lg}; padding: ${theme.spacing[4]}; text-align: center;`;
const MktVal = styled.div`font-size: ${theme.typography['2xl']}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary};`;
const MktLbl = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; margin-top: ${theme.spacing[1]};`;
const ChartEmpty = styled.div`height: 140px; display: flex; align-items: center; justify-content: center; color: ${theme.colors.textMuted}; font-size: ${theme.typography.sm};`;
const TableScroll = styled.div`overflow-x: auto; -webkit-overflow-scrolling: touch;`;
const AgentTable = styled.table`width: 100%; min-width: 560px; border-collapse: collapse; font-size: ${theme.typography.sm};`;
const Th = styled.th`padding: ${theme.spacing[2]} ${theme.spacing[3]}; text-align: ${p => p.align || 'left'}; color: ${theme.colors.textMuted}; font-weight: ${theme.typography.medium}; border-bottom: 1px solid ${theme.colors.border};`;
const Td = styled.td`padding: ${theme.spacing[2]} ${theme.spacing[3]}; text-align: ${p => p.align || 'left'}; border-bottom: 1px solid ${theme.colors.gray100};`;
const AgentChip = styled.span`background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; padding: 2px ${theme.spacing[2]}; border-radius: ${theme.radii.full}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.medium};`;

const LabelRow = styled.span`display: inline-flex; align-items: center; gap: 4px;`;
const HelpIcon = styled(HelpCircle)`color: ${theme.colors.textMuted}; cursor: help; flex-shrink: 0;`;

// StatLabel com ícone de ajuda e tooltip nativo do browser (title)
function LabelWithHelp({ label, help }) {
  return (
    <LabelRow>
      {label}
      <HelpIcon size={13} title={help} aria-label={help} />
    </LabelRow>
  );
}

export default ManagerDashboard;
